import { useEffect, useCallback, useRef, useState } from 'react';
import { AppShell, Box, Group, Title } from '@mantine/core';
import { ColorSchemeToggle, Container } from './components/ui';
import { HomePage } from './pages/HomePage';
import { TickersPage } from './pages/tickers/TickersPage';
import { useUiStore, type Page } from './lib/ui-store';

const HEADER_HEIGHT = 60;
const NAV_FADE_MS = 5_000;

function scrollToSection(page: Page) {
  document.getElementById(`section-${page}`)?.scrollIntoView({ behavior: 'smooth' });
}

function NavLink({ page, label }: { page: Page; label: string }) {
  const { page: activePage } = useUiStore();
  return (
    <button
      className="nav-link"
      data-active={activePage === page ? 'true' : 'false'}
      onClick={() => scrollToSection(page)}
      aria-current={activePage === page ? 'page' : undefined}
    >
      {label}
    </button>
  );
}

export function App() {
  const setPage = useUiStore((s) => s.setPage);
  const [navVisible, setNavVisible] = useState(true);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isScrolledRef = useRef(false);

  const clearFadeTimer = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  // Show nav and restart the idle fade timer (mobile only when scrolled).
  const showNav = useCallback(() => {
    setNavVisible(true);
    clearFadeTimer();
    if (isScrolledRef.current && window.matchMedia('(max-width: 768px)').matches) {
      fadeTimerRef.current = setTimeout(() => setNavVisible(false), NAV_FADE_MS);
    }
  }, [clearFadeTimer]);

  // Scroll spy + fade trigger.
  const checkScroll = useCallback(() => {
    const el = document.getElementById('section-tickers');
    if (!el) return;

    const scrolled = window.scrollY > HEADER_HEIGHT;
    const wasScrolled = isScrolledRef.current;
    isScrolledRef.current = scrolled;

    if (!scrolled) {
      // Back at top — always show, cancel any pending fade.
      setNavVisible(true);
      clearFadeTimer();
    } else if (!wasScrolled) {
      // Just crossed the header threshold — begin idle cycle.
      showNav();
    }

    setPage(el.getBoundingClientRect().top <= window.innerHeight / 2 ? 'tickers' : 'home');
  }, [setPage, clearFadeTimer, showNav]);

  useEffect(() => {
    window.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('touchstart', showNav, { passive: true });
    // Defer the initial sync so setState is called in a callback, not the effect body.
    const syncId = setTimeout(checkScroll, 0);
    return () => {
      clearTimeout(syncId);
      window.removeEventListener('scroll', checkScroll);
      window.removeEventListener('touchstart', showNav);
      clearFadeTimer();
    };
  }, [checkScroll, showNav, clearFadeTimer]);

  return (
    <AppShell header={{ height: HEADER_HEIGHT }} padding={0}>
      <AppShell.Header
        className="app-header"
        style={{
          opacity: navVisible ? 1 : 0,
          pointerEvents: navVisible ? undefined : 'none',
          transition: 'opacity 0.4s ease',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}
      >
        <Container h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
                <img src="/favicon.svg" width={36} height={36} alt="" aria-hidden="true" />
                <Box visibleFrom="sm">
                  <Title order={1} size="h3">TickerBuzz</Title>
                </Box>
              </Group>
              <nav aria-label="Main navigation">
                <Group gap="xs" wrap="nowrap">
                  <NavLink page="home" label="Home" />
                  <NavLink page="tickers" label="Tickers" />
                </Group>
              </nav>
            </Group>
            <ColorSchemeToggle />
          </Group>
        </Container>
      </AppShell.Header>

      <AppShell.Main>
        <section id="section-home" className="scroll-section">
          <HomePage />
        </section>

        <section id="section-tickers" className="scroll-section">
          <Container py="md">
            <TickersPage />
          </Container>
        </section>
      </AppShell.Main>
    </AppShell>
  );
}
